import { useMemo } from 'react';
import { Edge, MarkerType } from '@xyflow/react';
import { FlowSummary, L7AccessLogSummary } from '../../../api/client';
import {
    ExpansionViewState,
    ClusterCacheEntry,
    NamespaceCacheEntry,
    AwsAccountCacheEntry,
    OciTenancyCacheEntry,
    isCloudResourceKind,
} from '../types';
import { COLORS, FLOWS } from '../constants';
import { isValidVpcId } from '../utils';

// Custom edge type with our data
type FlowEdge = Edge<FlowEdgeData>;

// ============================================================================
// TYPES
// ============================================================================

export interface UseFlowEdgesInput {
    viewState: ExpansionViewState;
    clusterCache: Map<string, ClusterCacheEntry>;
    namespaceCache: Map<string, NamespaceCacheEntry>;
    awsAccountCache: Map<string, AwsAccountCacheEntry>;
    ociTenancyCache: Map<string, OciTenancyCacheEntry>;
    visibleNodeIds: Set<string>;
}

export interface FlowEdgeData extends Record<string, unknown> {
    flowType: 'l3l4' | 'l7';
    protocol: string;
    port: number;
    allowed: boolean;
    suspicious?: boolean;
    count: number;
    srcName: string;
    dstName: string;
    srcNamespace: string;
    dstNamespace: string;
    srcKind?: string;
    dstKind?: string;
    // Original flow data for the dialog
    originalFlow?: FlowSummary | L7AccessLogSummary;
    originalFlows?: (FlowSummary | L7AccessLogSummary)[];
}

// ============================================================================
// HELPER: Determine edge color based on flow status
// ============================================================================

function getEdgeColor(allowed: boolean, suspicious?: boolean): string {
    if (suspicious) return COLORS.flowSuspicious;
    return allowed ? COLORS.flowAllowed : COLORS.flowDenied;
}

// ============================================================================
// HELPER: Resolve cloud resource endpoint to visible node ID
// ============================================================================

// Helper to resolve a found resource to the appropriate node ID based on expansion state
function resolveResourceToNodeId(
    resource: { id: string; vpc_id?: string },
    viewState: ExpansionViewState
): string {
    if (isValidVpcId(resource.vpc_id)) {
        // Resource belongs to a VPC
        if (viewState.expandedVpcs.has(resource.vpc_id!)) {
            // VPC expanded - route to resource node
            return resource.id;
        }
        // VPC collapsed - route to VPC node
        return `vpc:${resource.vpc_id}`;
    } else {
        // Global resource (no valid VPC ID) - route directly to resource node
        // since global resources are always shown when account is expanded
        return resource.id;
    }
}

function resolveCloudResourceToNodeId(
    awsAccountId: string,
    resourceName: string,
    kind: string,
    viewState: ExpansionViewState,
    awsAccountCache: Map<string, AwsAccountCacheEntry>,
    ip?: string  // Optional IP address for VPC Flow Log correlation
): string | null {
    // If AWS account not expanded, roll up to account node
    // Use colon separator to match node IDs from useLayoutEngine
    if (!viewState.expandedAwsAccounts.has(awsAccountId)) {
        return `aws-account:${awsAccountId}`;
    }

    // AWS account is expanded - try to find the specific resource
    const accountData = awsAccountCache.get(awsAccountId);
    if (!accountData) {
        return `aws-account:${awsAccountId}`;
    }

    // FIRST: Try IP-based lookup (most reliable for VPC Flow Logs)
    if (ip && accountData.ipToResourceMap) {
        const resourceId = accountData.ipToResourceMap.get(ip);
        if (resourceId) {
            const resource = accountData.resources.find(r => r.id === resourceId);
            if (resource) {
                return resolveResourceToNodeId(resource, viewState);
            }
        }
    }

    // SECOND: Try name-based lookup (fallback for flows with resource names)
    const resource = accountData.resources.find(r => {
        const rName = r.name || r.resource_id;
        return rName === resourceName;
    });

    if (resource) {
        return resolveResourceToNodeId(resource, viewState);
    }

    // Fallback to account node (resource not found in cache)
    return `aws-account:${awsAccountId}`;
}

// Helper to resolve OCI VCN resource to node ID based on expansion state
function resolveOciVcnResourceToNodeId(
    resource: { id: string; vpc_id?: string },
    viewState: ExpansionViewState
): string {
    if (resource.vpc_id) {
        // Resource belongs to a VCN
        if (viewState.expandedVcns.has(resource.vpc_id)) {
            // VCN expanded - route to resource node
            return resource.id;
        }
        // VCN collapsed - route to VCN node
        return `vcn:${resource.vpc_id}`;
    } else {
        // Global resource (no VCN) - route directly to resource node
        return resource.id;
    }
}

function resolveOciResourceToNodeId(
    ociTenancyOcid: string,
    resourceName: string,
    kind: string,
    viewState: ExpansionViewState,
    ociTenancyCache: Map<string, OciTenancyCacheEntry>,
    ip?: string  // Optional IP address for VCN Flow Log correlation
): string | null {
    // If OCI tenancy not expanded, roll up to tenancy node
    if (!viewState.expandedOciTenancies.has(ociTenancyOcid)) {
        return `oci-tenancy:${ociTenancyOcid}`;
    }

    // OCI tenancy is expanded - try to find the specific resource
    const tenancyData = ociTenancyCache.get(ociTenancyOcid);
    if (!tenancyData) {
        return `oci-tenancy:${ociTenancyOcid}`;
    }

    // FIRST: Try IP-based lookup (most reliable for VCN Flow Logs)
    if (ip && tenancyData.ipToResourceMap) {
        const resourceId = tenancyData.ipToResourceMap.get(ip);
        if (resourceId) {
            const resource = tenancyData.resources.find(r => r.id === resourceId);
            if (resource) {
                return resolveOciVcnResourceToNodeId(resource, viewState);
            }
        }
    }

    // SECOND: Try name-based lookup (fallback for flows with resource names)
    const resource = tenancyData.resources.find(r => {
        const rName = r.name || r.resource_id;
        return rName === resourceName;
    });

    if (resource) {
        return resolveOciVcnResourceToNodeId(resource, viewState);
    }

    // Fallback to tenancy node (resource not found in cache)
    return `oci-tenancy:${ociTenancyOcid}`;
}

// ============================================================================
// HELPER: Resolve flow endpoint to visible node ID
// ============================================================================

// Helper to check if a node is visible (either in visibleNodeIds or a special node type)
function isNodeVisible(nodeId: string, visibleNodeIds: Set<string>): boolean {
    return visibleNodeIds.has(nodeId) ||
        nodeId.startsWith('external') ||
        nodeId.startsWith('known-ip') ||
        nodeId.startsWith('aws-') ||
        nodeId.startsWith('vpc:') ||
        nodeId.startsWith('oci-') ||
        nodeId.startsWith('vcn:');
}

// Helper to detect if a namespace is an OCI tenancy OCID
function isOciTenancyOcid(ns: string): boolean {
    return ns.startsWith('ocid1.tenancy.oc1.');
}

function resolveEndpointToNodeId(
    namespace: string,
    name: string,
    kind: string,
    clusterUID: string,
    viewState: ExpansionViewState,
    clusterCache: Map<string, ClusterCacheEntry>,
    namespaceCache: Map<string, NamespaceCacheEntry>,
    awsAccountCache: Map<string, AwsAccountCacheEntry>,
    flowContext?: { awsAccountId?: string; visibleNodeIds?: Set<string>; ip?: string; ociTenancyOcid?: string; ociTenancyCache?: Map<string, OciTenancyCacheEntry> }
): string | null {
    // Check if this is a cloud resource endpoint
    if (isCloudResourceKind(kind)) {
        // Check if this is an OCI resource (namespace is tenancy OCID)
        if (isOciTenancyOcid(namespace) && flowContext?.ociTenancyCache) {
            return resolveOciResourceToNodeId(
                namespace, // OCI tenancy OCID
                name,      // Resource name
                kind,
                viewState,
                flowContext.ociTenancyCache,
                flowContext?.ip  // Pass IP for VCN Flow Log correlation
            );
        }
        // Otherwise treat as AWS resource (namespace is AWS account ID)
        return resolveCloudResourceToNodeId(
            namespace, // AWS account ID
            name,      // Resource name
            kind,
            viewState,
            awsAccountCache,
            flowContext?.ip  // Pass IP for VPC Flow Log correlation
        );
    }

    // External traffic
    if (namespace === '' || namespace === 'external' || kind === 'External' || kind === 'Unmanaged') {
        if (name === 'reserved:world') {
            return null; // Skip world endpoints for now
        }

        // Use the raw IP address if available (preferred), otherwise fall back to name
        // Node IDs are created using src_ip/dst_ip, so we need to match on that
        const identifier = flowContext?.ip || name;

        // Check if known-ip node exists first (for IPs with friendly names)
        // This works for both AWS and K8s flows
        const knownIpNodeId = `known-ip|${encodeURIComponent(identifier)}`;
        if (flowContext?.visibleNodeIds?.has(knownIpNodeId)) {
            return knownIpNodeId;
        }

        // Route to individual external IP node or aggregate
        // Known IPs use format `known-ip|${encodeURIComponent(ip)}`
        // Individual external nodes use format `external-ip|${encodeURIComponent(ip)}`, aggregate uses `external`
        // Note: When ≤25 external IPs, only individual nodes exist (no aggregate)
        //       When >25 external IPs, only aggregate node exists (no individual nodes)

        // Check if individual external-ip node exists (use encodeURIComponent for IPv6 safety)
        const individualNodeId = `external-ip|${encodeURIComponent(identifier)}`;
        if (flowContext?.visibleNodeIds?.has(individualNodeId)) {
            return individualNodeId;
        }
        // Check if per-source aggregate exists (>25 external IPs from one source)
        // Source IDs match useLayoutEngine.ts: aws-account:${id}, oci-tenancy:${ocid}, or clusterUID
        const sourceId = flowContext?.awsAccountId
            ? `aws-account:${flowContext.awsAccountId}`
            : flowContext?.ociTenancyOcid
            ? `oci-tenancy:${flowContext.ociTenancyOcid}`
            : clusterUID;
        const perSourceAggId = `external-agg|${sourceId}`;
        if (flowContext?.visibleNodeIds?.has(perSourceAggId)) {
            return perSourceAggId;
        }
        // Check if global aggregate external node exists as final fallback
        if (flowContext?.visibleNodeIds?.has('external')) {
            return 'external';
        }
        // Neither exists - skip this edge (external IP not in topology)
        return null;
    }

    // Find namespace ID from name
    const clusterData = clusterCache.get(clusterUID);
    if (!clusterData) {
        return null;
    }

    const namespaceObj = clusterData.namespaces.find(ns => ns.name === namespace);
    if (!namespaceObj) {
        // Namespace not in this cluster - might be external namespace
        return `external-namespace-${namespace}`;
    }

    const namespaceID = namespaceObj.id;

    // "Namespace" kind endpoints come from cluster-level namespace-to-namespace aggregated flows.
    // These always resolve to the namespace container — no workload/service validation needed.
    if (kind === 'Namespace') {
        return namespaceID;
    }

    // Try to resolve the endpoint name to a workload/service in the namespace cache.
    // Returns the matched resource ID, or null if the cache is loaded but no match found.
    // Returns undefined if the cache isn't loaded yet (can't validate).
    const nsCache = namespaceCache.get(namespaceID);
    let resolvedResourceId: string | null | undefined = undefined; // undefined = cache not loaded
    if (nsCache) {
        resolvedResourceId = null; // cache loaded, assume not found

        // Try to find the workload by exact name match
        const workload = nsCache.workloads.find(w => w.name === name);
        if (workload) {
            resolvedResourceId = workload.id || workload.uid;
        }

        // Try to find service by exact name match
        if (!resolvedResourceId) {
            const service = nsCache.services.find(s => s.name === name);
            if (service) {
                resolvedResourceId = service.id;
            }
        }

        // Pod names often have format: {deployment-name}-{replicaset-hash}-{pod-id}
        // Try to find a workload whose name is a prefix of the pod name
        // This handles cases where flow has pod name but workload cache has deployment name
        if (!resolvedResourceId && kind === 'Pod' && name.includes('-')) {
            // Pod names have format: {deployment-name}-{replicaset-hash}-{pod-id}
            // Find the workload with the longest matching prefix to handle overlapping names
            // e.g., pod "api-gateway-abc123-xyz" should match "api-gateway" over "api"
            const workloadByPrefix = nsCache.workloads
                .filter(w => name.startsWith(w.name + '-'))
                .sort((a, b) => b.name.length - a.name.length)[0];
            if (workloadByPrefix) {
                resolvedResourceId = workloadByPrefix.id || workloadByPrefix.uid;
            }
        }
    }

    // Check if namespace is expanded
    if (viewState.expandedNamespaces.has(namespaceID)) {
        // Namespace is expanded - return the resolved workload/service ID,
        // or null if not found. Hides edges for Istio flows that have no
        // destination resource and fall back to the namespace name.
        return resolvedResourceId ?? null;
    }

    // Namespace is collapsed - roll up to namespace container,
    // but only if the endpoint is valid (resolvable or cache not loaded yet).
    // If the cache is loaded and the endpoint doesn't match any workload/service,
    // return null to hide the edge consistently in both expanded and collapsed states.
    if (resolvedResourceId === null) {
        return null;
    }
    return namespaceID;
}

// ============================================================================
// HELPER: Process L3/L4 flows
// ============================================================================

function processL3L4Flows(
    flows: FlowSummary[],
    clusterUID: string,
    viewState: ExpansionViewState,
    clusterCache: Map<string, ClusterCacheEntry>,
    namespaceCache: Map<string, NamespaceCacheEntry>,
    awsAccountCache: Map<string, AwsAccountCacheEntry>,
    visibleNodeIds: Set<string>,
    flowContext?: { awsAccountId?: string; visibleNodeIds?: Set<string>; ociTenancyOcid?: string; ociTenancyCache?: Map<string, OciTenancyCacheEntry> }
): FlowEdge[] {
    const edges: FlowEdge[] = [];
    const edgeMap = new Map<string, FlowEdge>();

    flows.forEach((flow) => {
        const sourceId = resolveEndpointToNodeId(
            flow.src_namespace || '',
            flow.src_name || '',
            flow.src_kind || '',
            clusterUID,
            viewState,
            clusterCache,
            namespaceCache,
            awsAccountCache,
            { ...flowContext, ip: flow.src_ip }
        );

        const targetId = resolveEndpointToNodeId(
            flow.dst_namespace || '',
            flow.dst_name || '',
            flow.dst_kind || '',
            clusterUID,
            viewState,
            clusterCache,
            namespaceCache,
            awsAccountCache,
            { ...flowContext, ip: flow.dst_ip }
        );

        if (!sourceId || !targetId) return;
        if (sourceId === targetId) return; // Skip self-loops

        // Check if both nodes are visible
        if (!isNodeVisible(sourceId, visibleNodeIds) || !isNodeVisible(targetId, visibleNodeIds)) return;

        const edgeKey = `${sourceId}-${targetId}`;
        const existingEdge = edgeMap.get(edgeKey);

        if (existingEdge && existingEdge.data) {
            // Aggregate with existing edge
            const data = existingEdge.data as unknown as FlowEdgeData;
            data.count += flow.count || 1;
            // Collect original flows for the dialog
            if (!data.originalFlows) {
                data.originalFlows = [];
            }
            data.originalFlows.push(flow);
        } else {
            const edge: FlowEdge = {
                id: `flow-${edgeKey}`,
                source: sourceId,
                target: targetId,
                type: 'smoothstep',
                animated: true,
                zIndex: 0,
                style: {
                    stroke: getEdgeColor(flow.allowed ?? true, flow.suspicious),
                    strokeWidth: 2,
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: getEdgeColor(flow.allowed ?? true, flow.suspicious),
                },
                data: {
                    flowType: 'l3l4',
                    protocol: flow.protocol || 'TCP',
                    port: flow.port || 0,
                    allowed: flow.allowed ?? true,
                    suspicious: flow.suspicious,
                    count: flow.count || 1,
                    srcName: flow.src_name || '',
                    dstName: flow.dst_name || '',
                    srcNamespace: flow.src_namespace || '',
                    dstNamespace: flow.dst_namespace || '',
                    srcKind: flow.src_kind || '',
                    dstKind: flow.dst_kind || '',
                    originalFlow: flow,
                    originalFlows: [flow],
                }
            };
            edgeMap.set(edgeKey, edge);
        }
    });

    edgeMap.forEach(edge => edges.push(edge));
    return edges;
}

// ============================================================================
// HELPER: Process L7 access logs
// ============================================================================

function processL7Logs(
    logs: L7AccessLogSummary[],
    clusterUID: string,
    viewState: ExpansionViewState,
    clusterCache: Map<string, ClusterCacheEntry>,
    namespaceCache: Map<string, NamespaceCacheEntry>,
    awsAccountCache: Map<string, AwsAccountCacheEntry>,
    visibleNodeIds: Set<string>,
    flowContext?: { awsAccountId?: string; visibleNodeIds?: Set<string> }
): FlowEdge[] {
    const edges: FlowEdge[] = [];
    const edgeMap = new Map<string, FlowEdge>();

    logs.forEach(log => {
        const sourceId = resolveEndpointToNodeId(
            log.src_namespace || '',
            log.src_name || '',
            log.src_kind || '',
            clusterUID,
            viewState,
            clusterCache,
            namespaceCache,
            awsAccountCache,
            { ...flowContext, ip: log.src_ip }
        );

        const targetId = resolveEndpointToNodeId(
            log.dst_namespace || '',
            log.dst_name || '',
            log.dst_kind || '',
            clusterUID,
            viewState,
            clusterCache,
            namespaceCache,
            awsAccountCache,
            { ...flowContext, ip: log.dst_ip }
        );

        if (!sourceId || !targetId) return;
        if (sourceId === targetId) return; // Skip self-loops

        // Check if both nodes are visible
        if (!isNodeVisible(sourceId, visibleNodeIds) || !isNodeVisible(targetId, visibleNodeIds)) return;

        const edgeKey = `l7-${sourceId}-${targetId}`;
        const existingEdge = edgeMap.get(edgeKey);

        if (existingEdge && existingEdge.data) {
            // Aggregate with existing edge
            const data = existingEdge.data as unknown as FlowEdgeData;
            data.count += log.count || 1;
            // Collect original logs for the dialog
            if (!data.originalFlows) {
                data.originalFlows = [];
            }
            data.originalFlows.push(log);
        } else {
            const edge: FlowEdge = {
                id: `flow-${edgeKey}`,
                source: sourceId,
                target: targetId,
                type: 'smoothstep',
                animated: true,
                zIndex: 0,
                style: {
                    stroke: getEdgeColor(log.allowed ?? true, log.suspicious),
                    strokeWidth: 2,
                    strokeDasharray: '5,5', // Dashed line for L7
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: getEdgeColor(log.allowed ?? true, log.suspicious),
                },
                data: {
                    flowType: 'l7',
                    protocol: log.l7_protocol || 'HTTP',
                    port: log.port || 0,
                    allowed: log.allowed ?? true,
                    suspicious: log.suspicious,
                    count: log.count || 1,
                    srcName: log.src_name || '',
                    dstName: log.dst_name || '',
                    srcNamespace: log.src_namespace || '',
                    dstNamespace: log.dst_namespace || '',
                    srcKind: log.src_kind || '',
                    dstKind: log.dst_kind || '',
                    originalFlow: log,
                    originalFlows: [log],
                }
            };
            edgeMap.set(edgeKey, edge);
        }
    });

    edgeMap.forEach(edge => edges.push(edge));
    return edges;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useFlowEdges(input: UseFlowEdgesInput): FlowEdge[] {
    const { viewState, clusterCache, namespaceCache, awsAccountCache, ociTenancyCache, visibleNodeIds } = input;

    // Serialize Sets to arrays for stable dependency comparison
    const expandedClustersArray = useMemo(
        () => Array.from(viewState.expandedClusters).sort(),
        [viewState.expandedClusters]
    );
    const expandedNamespacesArray = useMemo(
        () => Array.from(viewState.expandedNamespaces).sort(),
        [viewState.expandedNamespaces]
    );
    const expandedAwsAccountsArray = useMemo(
        () => Array.from(viewState.expandedAwsAccounts).sort(),
        [viewState.expandedAwsAccounts]
    );
    const expandedOciTenanciesArray = useMemo(
        () => Array.from(viewState.expandedOciTenancies).sort(),
        [viewState.expandedOciTenancies]
    );
    const visibleNodeIdsArray = useMemo(
        () => Array.from(visibleNodeIds).sort(),
        [visibleNodeIds]
    );

    return useMemo(() => {
        const allEdges: FlowEdge[] = [];

        // Process flows from each expanded cluster
        // Include BOTH cluster-level AND all namespace-level flows to show all traffic
        // This ensures external flow lines remain stable when drilling into/out of namespaces
        viewState.expandedClusters.forEach(clusterUID => {
            const clusterData = clusterCache.get(clusterUID);
            if (!clusterData) return;

            // Process cluster-level flows (namespace-to-namespace)
            // Pass visibleNodeIds in flowContext for known-ip and external-ip routing
            const clusterEdges = processL3L4Flows(
                clusterData.flows,
                clusterUID,
                viewState,
                clusterCache,
                namespaceCache,
                awsAccountCache,
                visibleNodeIds,
                { visibleNodeIds }
            );
            allEdges.push(...clusterEdges);

            // Process cluster-level L7 logs
            const clusterL7Edges = processL7Logs(
                clusterData.l7Logs,
                clusterUID,
                viewState,
                clusterCache,
                namespaceCache,
                awsAccountCache,
                visibleNodeIds,
                { visibleNodeIds }
            );
            allEdges.push(...clusterL7Edges);

            // Also process ALL namespace-level flows for this cluster
            // This ensures all external traffic is visible regardless of namespace expansion state
            namespaceCache.forEach(nsData => {
                if (nsData?.clusterUID !== clusterUID) return;

                const nsEdges = processL3L4Flows(
                    nsData.flows,
                    clusterUID,
                    viewState,
                    clusterCache,
                    namespaceCache,
                    awsAccountCache,
                    visibleNodeIds,
                    { visibleNodeIds }
                );
                allEdges.push(...nsEdges);

                const nsL7Edges = processL7Logs(
                    nsData.l7Logs,
                    clusterUID,
                    viewState,
                    clusterCache,
                    namespaceCache,
                    awsAccountCache,
                    visibleNodeIds,
                    { visibleNodeIds }
                );
                allEdges.push(...nsL7Edges);
            });
        });

        // Process flows from each expanded AWS account (VPC flow logs)
        viewState.expandedAwsAccounts.forEach(accountId => {
            const accountData = awsAccountCache.get(accountId);
            if (!accountData || !accountData.flows) return;

            // Process AWS account flows (cloud resource to cloud resource / external)
            // Pass empty clusterUID since these flows aren't cluster-bound
            // Pass flowContext with awsAccountId and visibleNodeIds for external IP routing
            const awsEdges = processL3L4Flows(
                accountData.flows,
                '', // No cluster context for AWS account flows
                viewState,
                clusterCache,
                namespaceCache,
                awsAccountCache,
                visibleNodeIds,
                { awsAccountId: accountId, visibleNodeIds }
            );
            allEdges.push(...awsEdges);
        });

        // Process flows from each expanded OCI tenancy (VCN flow logs)
        viewState.expandedOciTenancies.forEach(tenancyOcid => {
            const tenancyData = ociTenancyCache.get(tenancyOcid);
            if (!tenancyData || !tenancyData.flows) return;

            // Process OCI tenancy flows (cloud resource to cloud resource / external)
            // Pass flowContext with ociTenancyOcid and visibleNodeIds for external IP routing
            const ociEdges = processL3L4Flows(
                tenancyData.flows,
                '', // No cluster context for OCI flows
                viewState,
                clusterCache,
                namespaceCache,
                awsAccountCache,
                visibleNodeIds,
                { ociTenancyOcid: tenancyOcid, visibleNodeIds, ociTenancyCache }
            );
            allEdges.push(...ociEdges);
        });

        // Deduplicate edges by ID
        const uniqueEdges = new Map<string, FlowEdge>();
        allEdges.forEach(edge => {
            const existing = uniqueEdges.get(edge.id);
            if (existing && existing.data && edge.data) {
                // Merge counts
                existing.data.count += edge.data.count;
            } else {
                uniqueEdges.set(edge.id, edge);
            }
        });

        // Limit edges for performance
        const sortedEdges = Array.from(uniqueEdges.values())
            .sort((a, b) => {
                const aCount = a.data?.count || 0;
                const bCount = b.data?.count || 0;
                return bCount - aCount;
            })
            .slice(0, FLOWS.maxEdges);

        return sortedEdges;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewState, expandedClustersArray, expandedNamespacesArray, expandedAwsAccountsArray, expandedOciTenanciesArray, clusterCache, namespaceCache, awsAccountCache, ociTenancyCache, visibleNodeIdsArray]);
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    resolveEndpointToNodeId,
    processL3L4Flows,
    processL7Logs,
    getEdgeColor,
};
